/**
 * The half of "an observer may not break the call" that a `try`/`catch` misses.
 *
 * `Notify<E>` and `AfterRequestHandler<T>` both return `void`, and TypeScript
 * deliberately accepts an `async` function against such a signature -- otherwise
 * no async callback could be handed to `forEach`. That handler does not throw:
 * it RETURNS a rejected promise, so the `catch` around the call sees nothing and
 * the rejection escapes as an `unhandledRejection`. Node 22 ends the process on
 * that, and one process holds every plugin (IS3), so a logging hiccup in one
 * corner takes the backend with it.
 *
 * Nothing here awaits. A rejection is DROPPED, exactly as a synchronous throw is
 * already dropped (AF7, GA8), so the only behavior that changes is the one that
 * used to be fatal.
 */

/** Named at module scope because the handler shape is what the lint gate allows. */
const swallow = (): void => {};

/**
 * Whether this value is one that can settle, asked the way that survives a realm.
 *
 * NOT `value instanceof Promise`. That asks which CONSTRUCTOR made the value,
 * and the constructor is a different object in every realm -- two copies of one
 * package in a monorepo are two realms, and a promise from the other side fails
 * the check while behaving identically. This package already pays that price
 * once: `body.ts` reads internal tags for `FormData`, `Blob` and `ArrayBuffer`
 * for exactly this reason (BA33, BA34, BA35).
 *
 * NOT that tag either, though, which is the part worth saying out loud.
 * `Object.prototype.toString.call` answers `[object Promise]` for a NATIVE
 * promise and nothing useful for any other thenable, so reusing the `body.ts`
 * form here would trade one blind spot for a narrower one. A callable `then` is
 * what the language itself looks for when it awaits: it answers the same in
 * every realm AND for the hand-rolled thenable a test double tends to be.
 */
function isThenable(value: unknown): value is PromiseLike<unknown> {
  if (value === null) return false;
  if (typeof value !== 'object' && typeof value !== 'function') return false;
  return 'then' in value && typeof value.then === 'function';
}

/**
 * Takes whatever an observer handed back and makes sure it cannot end anything.
 *
 * The guard lives HERE rather than at each call site, so that a call site cannot
 * hold the dangerous half without the guard: `dropRejection(handler(args))` is
 * one expression, and there is no way to write the call and forget the catch.
 *
 * `Promise.resolve(...)` rather than `returned.catch(...)`, because a thenable is
 * only ever promised a `then`. Calling `catch` on one that has none would throw
 * a `TypeError` instead of handling anything, the surrounding `catch` would eat
 * that, and the rejection this exists to swallow would still escape.
 */
export function dropRejection(returned: unknown): void {
  if (!isThenable(returned)) return;
  Promise.resolve(returned).catch(swallow);
}
